import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { initializePushNotifications, unregisterPushNotifications } from "@/lib/pushNotifications";
import { Capacitor } from "@capacitor/core";
import { clearBookingCache } from "@/lib/bookingCache";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata: SignUpMetadata) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

export interface SignUpMetadata {
  full_name: string;
  phone: string;
  account_type: "particular" | "empresa";
  user_role?: "host" | "guest";
  nif_number?: string;
  company_name?: string;
  country?: string;
  city?: string;
  detected_country_code?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | null = null;
    const isNative = Capacitor.isNativePlatform();

    (async () => {
      try {
        // For native apps, add timeout to prevent hanging
        const sessionPromise = supabase.auth.getSession();
        const sessionResult = isNative 
          ? await Promise.race([
              sessionPromise,
              new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Session timeout')), 10000)
              )
            ]).catch(() => ({ data: { session: null } }))
          : await sessionPromise;

        if (cancelled) return;

        setSession(sessionResult.data.session);
        setUser(sessionResult.data.session?.user ?? null);
      } catch (e) {
        console.error('[Auth] Error getting session:', e);
        // Don't block the app on auth errors
        setSession(null);
        setUser(null);
      } finally {
        setLoading(false);
      }

      const { data } = supabase.auth.onAuthStateChange(async (_event, nextSession) => {
        if (cancelled) return;
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        
        // Initialize push notifications when user logs in
        if (nextSession?.user) {
          try {
            await initializePushNotifications(nextSession.user.id);
          } catch (e) {
            // Don't let push notification errors crash the app
            console.error('[Auth] Push notification init failed:', e);
          }
          // Force version check on sign-in so returning users get the latest build
          if (_event === "SIGNED_IN") {
            try {
              const { forceVersionCheckAndReload } = await import("@/lib/appVersion");
              // Fire & forget — will reload if newer
              forceVersionCheckAndReload().catch(() => {});
            } catch { /* ignore */ }
          }
        }
      });
      subscription = data.subscription;
    })();

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const isNative = Capacitor.isNativePlatform();
    
    // For native apps, add timeout to prevent hanging on slow networks
    if (isNative) {
      const timeoutMs = 15000; // 15 seconds timeout
      
      try {
        const result = await Promise.race([
          supabase.auth.signInWithPassword({ email, password }),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Tempo limite excedido. Verifique sua conexão.')), timeoutMs)
          )
        ]);
        return { error: result.error };
      } catch (e) {
        return { error: e as Error };
      }
    }
    
    // Web version - normal flow
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, metadata: SignUpMetadata) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata,
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Unregister push notifications before logout
    if (user?.id) {
      await unregisterPushNotifications(user.id);
    }
    // Clear all booking cache to prevent deleted bookings from reappearing
    clearBookingCache();
    try {
      sessionStorage.removeItem("bera:2fa_verified");
    } catch {}
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth?mode=reset`,
    });
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
